/**
 * Auth API Client
 * Handles communication with the auth-service backend for
 * workspace members and invitations.
 */

const API_BASE = '/api/auth';

class ApiError extends Error {
  status: number;
  /** Top-level `code` of a JSON error body, when the server sent one. */
  code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const url = endpoint.startsWith('/')
    ? `${API_BASE}${endpoint}`
    : `${API_BASE}/${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  const token = localStorage.getItem('archie_access_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    body: options.body
      ? typeof options.body === 'string'
        ? options.body
        : JSON.stringify(options.body)
      : undefined,
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.text();
    let code: string | undefined;
    try {
      const parsed: unknown = JSON.parse(error);
      const raw = (parsed as { code?: unknown } | null)?.code;
      if (typeof raw === 'string') code = raw;
    } catch {
      // not JSON: message stays the raw text
    }
    throw new ApiError(response.status, error || response.statusText, code);
  }

  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface WorkspaceMember {
  id: string;
  userId: string;
  role: string;
  createdAt: string;
  user: {
    id: string;
    email: string;
    name: string | null;
    avatar: string | null;
  };
}

export interface MembersListResponse {
  data: WorkspaceMember[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface WorkspaceInvitation {
  id: string;
  email: string;
  role: string;
  token?: string;
  workspaceId: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  invitedBy?: {
    name: string | null;
    email: string;
  };
}

export interface InvitationsListResponse {
  data: WorkspaceInvitation[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  /** Key is stored encrypted and can be revealed; false for pre-feature keys */
  revealable?: boolean;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface ApiKeyCreateResponse {
  id: string;
  name: string;
  prefix: string;
  key: string;
  createdAt: string;
}

export interface ApiKeysListResponse {
  data: ApiKey[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface InvitationValidation {
  id: string;
  email: string;
  role: string;
  workspaceId: string;
  expiresAt: string;
  invitedBy: {
    name: string | null;
    email: string;
  };
}

// ── API ───────────────────────────────────────────────────────────────────────

export const authApi = {
  // Members
  listMembers: (wsId: string, limit = 20, offset = 0): Promise<MembersListResponse> =>
    request(`/workspaces/${wsId}/members?limit=${limit}&offset=${offset}`),

  addMember: (wsId: string, userId: string, role = 'member'): Promise<WorkspaceMember> =>
    request(`/workspaces/${wsId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId, role }),
    }),

  updateMemberRole: (wsId: string, memberId: string, role: string): Promise<WorkspaceMember> =>
    request(`/workspaces/${wsId}/members/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),

  removeMember: (wsId: string, memberId: string): Promise<void> =>
    request(`/workspaces/${wsId}/members/${memberId}`, {
      method: 'DELETE',
    }),

  // Invitations
  listInvitations: (wsId: string, limit = 20, offset = 0): Promise<InvitationsListResponse> =>
    request(`/workspaces/${wsId}/invitations?limit=${limit}&offset=${offset}`),

  createInvitation: (wsId: string, email: string, role = 'member'): Promise<WorkspaceInvitation> =>
    request(`/workspaces/${wsId}/invitations`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    }),

  validateInvitation: (token: string): Promise<InvitationValidation> =>
    request(`/invitations/${token}`),

  acceptInvitation: (token: string): Promise<{ success: boolean; workspaceId: string; role: string }> =>
    request(`/invitations/${token}/accept`, {
      method: 'POST',
    }),

  revokeInvitation: (wsId: string, invitationId: string): Promise<void> =>
    request(`/workspaces/${wsId}/invitations/${invitationId}`, {
      method: 'DELETE',
    }),

  // API Keys (PAT)
  listApiKeys: (limit = 20, offset = 0): Promise<ApiKeysListResponse> =>
    request(`/api-keys?limit=${limit}&offset=${offset}`),

  createApiKey: (name: string): Promise<ApiKeyCreateResponse> =>
    request('/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  revokeApiKey: (keyId: string): Promise<{ success: boolean; revokedAt: string }> =>
    request(`/api-keys/${keyId}`, {
      method: 'DELETE',
    }),

  revealApiKey: (keyId: string): Promise<{ id: string; name: string; key: string }> =>
    request(`/api-keys/${keyId}/reveal`),
};

export { ApiError };
